import { FC, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import "./dev.scss";

import { postOrgBootstrap } from "@rubi/api-generated";

import { useCommunities } from "@/hooks/useCommunities";

import { Breadcrumbs, Crumb, DevPage, ErrorMsg, Section } from "./common";
import { OrgBrandingPanel } from "./OrgBrandingPanel";
import { OrgDirectoryPanel } from "./OrgDirectoryPanel";
import { slugPreview } from "./slugPreview";

const DEFAULT_TIMEZONE = "America/Toronto";

const STEPS = ["Create", "Details & publish", "Branding", "Finish"];

export const OnboardCommunityWizard: FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [stytchOrgId, setStytchOrgId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1 — Create
  const [title, setTitle] = useState("");
  const [shortTitle, setShortTitle] = useState("");
  const [city, setCity] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [apiRegion, setApiRegion] = useState("ca");
  const [stytchSlug, setStytchSlug] = useState("");

  // services_url_slug is mandatory now — the citizen site addresses an org by
  // it — so the backend derives it from the title and rejects the request if
  // that collides, rather than leaving the column blank. Surface the clash here
  // so the operator overrides it before submitting instead of reading a 400.
  // The directory only lists public orgs, so this is a hint — the backend check
  // is authoritative. It spans both regions, as that check does: each region
  // holds only its own orgs, but citizen URLs resolve against both, so a slug
  // taken in the other region is taken.
  const communities = useCommunities();
  const [servicesSlug, setServicesSlug] = useState("");
  const derivedSlug = useMemo(() => slugPreview(title), [title]);
  const effectiveSlug = servicesSlug.trim() || derivedSlug;
  const slugClash = useMemo(
    () =>
      effectiveSlug === ""
        ? null
        : (communities.find((c) => c.services_url_slug === effectiveSlug) ??
          null),
    [communities, effectiveSlug],
  );

  const canCreate =
    title.trim().length > 0 &&
    city.trim().length > 0 &&
    contactEmail.trim().includes("@") &&
    effectiveSlug !== "" &&
    !slugClash &&
    !busy;

  const handleCreate = async () => {
    setBusy(true);
    setError(null);
    try {
      const response = await postOrgBootstrap({
        body: {
          title: title.trim(),
          short_title: shortTitle.trim() || null,
          city: city.trim(),
          contact_email: contactEmail.trim(),
          timezone: timezone.trim() || null,
          api_region: apiRegion,
          stytch_slug: stytchSlug.trim() || null,
          services_url_slug: servicesSlug.trim() || null,
          rubi_org_id: null,
        },
      });
      const result = response.data!;
      setOrgId(result.org.id);
      setStytchOrgId(result.stytch_org_id);
      setActiveStep(1);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create community");
    } finally {
      setBusy(false);
    }
  };

  const orgConfigPath = orgId ? `/dev_admin/org_config/${orgId}` : "/dev_admin";

  return (
    <DevPage
      title="Onboard community"
      breadcrumbs={
        <Breadcrumbs>
          <Crumb>
            <a href="/dev_admin">Dev admin</a>
          </Crumb>
          <Crumb>Onboard community</Crumb>
        </Breadcrumbs>
      }
    >
      <Section>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <ErrorMsg msg={error} />

        {activeStep === 0 && (
          <Stack spacing={2} sx={{ maxWidth: 560 }}>
            <Typography variant="body2" color="text.secondary">
              Creates the Stytch organization, the rubi_org row, and a default
              org config. Required fields are marked.
            </Typography>
            <TextField
              label="Title *"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <TextField
              label="Short title"
              value={shortTitle}
              onChange={(e) => setShortTitle(e.target.value)}
            />
            <TextField
              label="City *"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
            <TextField
              label="Contact email *"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
            />
            <TextField
              label="Timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            />
            <TextField
              select
              label="API region"
              value={apiRegion}
              onChange={(e) => setApiRegion(e.target.value)}
            >
              <MenuItem value="ca">ca</MenuItem>
              <MenuItem value="us">us</MenuItem>
            </TextField>
            <TextField
              label="Stytch slug (optional, derived from title if blank)"
              value={stytchSlug}
              onChange={(e) => setStytchSlug(e.target.value)}
            />
            <TextField
              label="Services URL slug (optional, derived from title if blank)"
              value={servicesSlug}
              onChange={(e) => setServicesSlug(e.target.value)}
              helperText="Lowercase letters, digits and single dashes. Citizens reach this community at /<slug>/online-reports."
            />
            {effectiveSlug !== "" &&
              (slugClash ? (
                <Alert severity="error">
                  Services URL slug <code>{effectiveSlug}</code> is already used
                  by {slugClash.title} (
                  {slugClash.api_region === "us" ? "US" : "Canadian"} region).
                  Every community needs its own — enter a different one to
                  continue.
                </Alert>
              ) : (
                <Alert severity="info">
                  Citizens will reach this community at{" "}
                  <code>/{effectiveSlug}</code>, and its citizen URLs are built
                  from it. Default language:{" "}
                  <code>{apiRegion === "us" ? "enUs" : "enCa"}</code>.
                </Alert>
              ))}
            <Box>
              <Button
                variant="contained"
                disabled={!canCreate}
                onClick={handleCreate}
              >
                {busy ? <CircularProgress size={20} /> : "Create community"}
              </Button>
            </Box>
          </Stack>
        )}

        {activeStep === 1 && orgId && (
          <Stack spacing={2}>
            <Alert severity="success">
              Created community <strong>{orgId}</strong>
              {stytchOrgId ? ` (Stytch ${stytchOrgId})` : ""}.
            </Alert>
            <OrgDirectoryPanel orgId={orgId} onSaved={() => setActiveStep(2)} />
          </Stack>
        )}

        {activeStep === 2 && orgId && (
          <Stack spacing={2} sx={{ maxWidth: 720 }}>
            <Typography variant="body2" color="text.secondary">
              Upload branding (optional). Each goes to S3 and is served from the
              community directory. Hero and login background may be a video.
            </Typography>
            <OrgBrandingPanel orgId={orgId} />
            <Box>
              <Button variant="contained" onClick={() => setActiveStep(3)}>
                Continue
              </Button>
            </Box>
          </Stack>
        )}

        {activeStep === 3 && (
          <Stack spacing={2} sx={{ maxWidth: 560 }}>
            <Alert severity="success">
              {title || "Community"} is set up. Finish SSO and review the full
              config below.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              SAML/SSO connections are managed per-org in the org config tool.
              Open it to add the IdP connection and finish onboarding.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                onClick={() => navigate(orgConfigPath)}
              >
                Set up SSO & review config
              </Button>
              <Button variant="outlined" onClick={() => navigate("/dev_admin")}>
                Done
              </Button>
            </Stack>
          </Stack>
        )}
      </Section>
    </DevPage>
  );
};

export default OnboardCommunityWizard;
